import { useEffect, useState } from 'react';

import { contactApi, type MyContact } from '../services/api';

type Editable = Pick<MyContact, 'address1' | 'address2' | 'city' | 'state' | 'zip' | 'telephone' | 'cell'>;

const EDITABLE_KEYS: (keyof Editable)[] = ['address1', 'address2', 'city', 'state', 'zip', 'telephone', 'cell'];

const emptyForm = (): Editable => ({
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  telephone: '',
  cell: '',
});

const fromContact = (c: MyContact): Editable =>
  Object.fromEntries(EDITABLE_KEYS.map((k) => [k, c[k] ?? ''])) as unknown as Editable;

/**
 * #2986 — rep self-service contact + address on the PWA profile. Thin UI over
 * /api/associates/me/contact/. Saving an address re-geocodes + sets the home
 * timezone server-side (no DM approval — it's factual contact data).
 */
export default function ContactSection() {
  const [data, setData] = useState<MyContact | null>(null);
  const [form, setForm] = useState<Editable>(emptyForm());
  const [noAssociate, setNoAssociate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const c = await contactApi.getMine();
      setData(c);
      setForm(fromContact(c));
    } catch (e) {
      if (e instanceof Error && e.message.includes('404')) setNoAssociate(true);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const setField = (k: keyof Editable, v: string) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const updated = await contactApi.update(form);
      setData(updated);
      setForm(fromContact(updated));
      const located = updated.lat && updated.lon;
      setMsg({
        text: located
          ? `Saved. Location updated${updated.timezone ? ` (${updated.timezone})` : ''}.`
          : 'Saved. We could not locate that address — check it, or your manager can review it.',
        err: false,
      });
    } catch {
      setMsg({ text: 'Could not save your details.', err: true });
    } finally {
      setBusy(false);
    }
  };

  if (noAssociate) return null;

  return (
    <section className="rounded-xl bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-left"
      >
        <h2 className="text-lg font-semibold text-slate-800">Contact &amp; Address</h2>
        <svg
          className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!open ? null : (
        <>
      <p className="mb-3 mt-2 text-xs text-slate-500">
        Keep your address current — it sets your home location and timezone used for scheduling.
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs text-slate-500 sm:col-span-2">
          Address
          <input type="text" value={form.address1 ?? ''} onChange={(e) => setField('address1', e.target.value)}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label className="text-xs text-slate-500 sm:col-span-2">
          Address 2 (apt, suite)
          <input type="text" value={form.address2 ?? ''} onChange={(e) => setField('address2', e.target.value)}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label className="text-xs text-slate-500">
          City
          <input type="text" value={form.city ?? ''} onChange={(e) => setField('city', e.target.value)}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-slate-500">
            State
            <input type="text" value={form.state ?? ''} onChange={(e) => setField('state', e.target.value)}
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-slate-500">
            ZIP
            <input type="text" value={form.zip ?? ''} onChange={(e) => setField('zip', e.target.value)}
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
        </div>
        <label className="text-xs text-slate-500">
          Phone
          <input type="tel" value={form.telephone ?? ''} onChange={(e) => setField('telephone', e.target.value)}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label className="text-xs text-slate-500">
          Cell
          <input type="tel" value={form.cell ?? ''} onChange={(e) => setField('cell', e.target.value)}
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button type="button" disabled={busy} onClick={save}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
        {data?.timezone && <span className="text-xs text-slate-400">Timezone: {data.timezone}</span>}
      </div>

      {msg && <p className={`mt-2 text-sm ${msg.err ? 'text-red-600' : 'text-green-700'}`}>{msg.text}</p>}
        </>
      )}
    </section>
  );
}
